/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    InventoryAccuracy,
    ProductDimensionAvailabilitySearchCriteria,
    ProductDimensionType,
    ProductDimensionValueInventoryAvailability,
    ProductsDataActions
} from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions, generateCacheKey, IKeyValuePair } from './utilities';
import { createInventoryAvailabilitySearchCriteria } from './utilities/product-inventory-utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Interface for IProductDimension.
 */
export interface IProductDimension {
    masterProductId: number;
    requestDimensionType?: ProductDimensionType;
    requestDimensionTypes?: ProductDimensionType[];
}

/**
 * Product Input.
 */
export class ProductsDimensionAvailabilitiesInput implements IActionInput {
    public masterProductId: number;

    public requestDimensionType: ProductDimensionType | undefined;

    public requestDimensionTypes: ProductDimensionType[] | undefined;

    public catalogId?: number;

    private _requestContext?: IRequestContext;

    public constructor(
        masterProductId: number,
        requestDimensionType?: ProductDimensionType,
        requestContext?: IRequestContext,
        requestDimensionTypes?: ProductDimensionType[]
    ) {
        this._requestContext = requestContext;
        this.masterProductId = masterProductId;
        this.requestDimensionType = requestDimensionType;
        this.requestDimensionTypes = requestDimensionTypes;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => {
        const keys: IKeyValuePair<string, number[] | number | undefined>[] = [
            { key: 'MasterProductId', value: this.masterProductId },
            { key: 'CatalogId', value: this.catalogId }
        ];
        if (this.requestDimensionTypes) {
            keys.push({ key: 'DimensionTypes', value: this.requestDimensionTypes });
        } else {
            keys.push({ key: 'DimensionType', value: this.requestDimensionType });
        }
        return generateCacheKey(this._requestContext?.apiSettings, ...keys);
    };

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType = (): string => 'MasterProductDimensionAvailabilities';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * Create input method for the getDimensionsForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
export const createProductsDimensionAvailabilitiesInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductsDimensionAvailabilitiesInput[] => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- ignore type of config.
    const productsDimensions: IProductDimension[] = inputData.config?.productsDimensions;

    return productsDimensions.map((productDimension: IProductDimension) => {
        return new ProductsDimensionAvailabilitiesInput(
            productDimension.masterProductId,
            productDimension.requestDimensionType,
            inputData.requestContext,
            productDimension.requestDimensionTypes
        );
    });
};

/**
 * Action Method for the getDimensionsForSelectedVariant Data Action.
 * @param inputs - Input for the data action.
 * @param context - Action context.
 * @returns Master product dimensions with availabilities.
 */
export async function getDimensionAvailabilitiesForProductsAction(
    inputs: ProductsDimensionAvailabilitiesInput[],
    context: IActionContext
): Promise<ProductDimensionValueInventoryAvailability[][]> {
    if (!Array.isArray(inputs) || !ArrayExtensions.hasElements(inputs)) {
        context.trace('[getDimensionAvailabilitiesForProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const promise = inputs.map(input => {
        // As per default PDP loads shipping inventory policies, so making swatch to  pick the same configuration
        const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(
            context,
            [input.masterProductId],
            true,
            undefined,
            undefined
        );

        const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
            MatchingDimensionValues: undefined,
            DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
            FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
            DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
            CatalogId: input.catalogId
        };

        if (input.requestDimensionTypes) {
            searchCriteria.RequestedDimensionTypeValues = input.requestDimensionTypes;
            searchCriteria.InventoryAccuracyValue = InventoryAccuracy.Low;
        } else {
            searchCriteria.RequestedDimensionTypeValue = input.requestDimensionType;
        }

        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            input.masterProductId,
            searchCriteria
        );

        return dimensionValuesPromise;
    });

    // eslint-disable-next-line no-return-await -- existing code
    return await Promise.all(promise);
}

/**
 * The GetDimensionAvailabilitiesForProductsAction
 * Gets the available product dimensions for the master product via the getDimensionValues RetailServer API.
 */
export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-dimension-availabilities-for-products',
    action: getDimensionAvailabilitiesForProductsAction as IAction<ProductDimensionValueInventoryAvailability[][]>,
    input: createProductsDimensionAvailabilitiesInput,
    isBatched: true
});
